import { apiClient } from '@features/shared/api/client'
import type {
  ExportFormat,
  ExportJob,
  ExportJobsResponse,
  ExportLinkResponse,
  ExportOptionsInput,
  ExportOptionsResponse,
  ExportPreviewResponse,
  ExportScope,
  ListExportRequest,
  QueueExportRequest,
  SaveExportDefaultsResponse,
  SendExportsRequest,
  SendExportsResponse,
} from './contracts'

/** A view id, or `all` for the table's All view. */
function viewPath(tableId: string, viewId: string | null): string {
  return `/tables/${tableId}/views/${viewId ?? 'all'}`
}

export async function queueExport(
  tableId: string,
  viewId: string | null,
  request: QueueExportRequest
): Promise<ExportJob> {
  const { data } = await apiClient.post<ExportJob>(`${viewPath(tableId, viewId)}/exports`, request)
  return data
}

export async function previewExport(
  tableId: string,
  viewId: string | null,
  request: QueueExportRequest,
  signal?: AbortSignal
): Promise<ExportPreviewResponse> {
  const { data } = await apiClient.post<ExportPreviewResponse>(
    `${viewPath(tableId, viewId)}/exports/preview`,
    request,
    { signal }
  )
  return data
}

/** Queue an export of rows the page holds (Automations, Users, Periods, ...). */
export async function queueListExport(request: ListExportRequest): Promise<ExportJob> {
  const { data } = await apiClient.post<ExportJob>('/exports', request)
  return data
}

export async function previewListExport(
  request: ListExportRequest,
  signal?: AbortSignal
): Promise<ExportPreviewResponse> {
  const { data } = await apiClient.post<ExportPreviewResponse>('/exports/preview', request, {
    signal,
  })
  return data
}

/** A format's options as one click exports them; `organization`: the organization's defaults. */
export async function getExportOptions(
  format: ExportFormat,
  signal?: AbortSignal,
  { organization = false }: { organization?: boolean } = {}
): Promise<ExportOptionsResponse> {
  const { data } = await apiClient.get<ExportOptionsResponse>('/exports/options', {
    params: organization ? { format, organization: true } : { format },
    signal,
  })
  return data
}

export async function saveExportDefaults(
  format: ExportFormat,
  options: ExportOptionsInput
): Promise<SaveExportDefaultsResponse> {
  const { data } = await apiClient.put<SaveExportDefaultsResponse>('/exports/options', {
    format,
    options,
  })
  return data
}

export async function listExports(
  scope: ExportScope,
  signal?: AbortSignal
): Promise<ExportJobsResponse> {
  const { data } = await apiClient.get<ExportJobsResponse>('/exports', {
    params: scopeParams(scope),
    signal,
  })
  return data
}

/** The person opened a menu of these exports: its finished ones are seen (no more blue dot). */
export async function markExportsSeen(scope: ExportScope): Promise<void> {
  await apiClient.post('/exports/seen', null, { params: scopeParams(scope) })
}

function scopeParams(scope: ExportScope): Record<string, string> {
  if ('tableId' in scope) return { table_id: scope.tableId }
  if ('list' in scope) return { list: scope.list }
  return {}
}

export async function getExport(exportId: string, signal?: AbortSignal): Promise<ExportJob> {
  const { data } = await apiClient.get<ExportJob>(`/exports/${exportId}`, { signal })
  return data
}

/**
 * A finished export's download link, absolute: a presigned S3 URL already
 * is; the API's own signed path (local development) is put under its base.
 */
export async function getExportLink(
  exportId: string,
  signal?: AbortSignal
): Promise<ExportLinkResponse> {
  const { data } = await apiClient.get<ExportLinkResponse>(`/exports/${exportId}/link`, {
    signal,
  })
  return absoluteLink(data)
}

/**
 * One zip of several finished exports, as a short-lived link: dragging more
 * than one Recent Exports row out (a browser drags out one file).
 */
export async function createExportBundle(
  exportIds: string[],
  signal?: AbortSignal
): Promise<ExportLinkResponse> {
  const { data } = await apiClient.post<ExportLinkResponse>(
    '/exports/bundle',
    { export_ids: exportIds },
    { signal }
  )
  return absoluteLink(data)
}

/** A presigned S3 URL is absolute already; the API's own signed path goes under its base. */
function absoluteLink(link: ExportLinkResponse): ExportLinkResponse {
  if (/^https?:\/\//i.test(link.url)) return link
  const base = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '')
  return { ...link, url: new URL(`${base}${link.url}`, window.location.origin).toString() }
}

export async function cancelExport(exportId: string): Promise<void> {
  await apiClient.delete(`/exports/${exportId}`)
}

/** Email the person's finished exports, attached to one email (Send). */
export async function sendExports(request: SendExportsRequest): Promise<SendExportsResponse> {
  const { data } = await apiClient.post<SendExportsResponse>('/exports/send', request)
  return data
}
